// The inquiries that CAME IN, in full — one row per customer request.
//
// This is the list behind the "New inquiries" number. That number alone was
// never actionable: a ₹90,000 engine request landing at 9am looked exactly
// like a ₹500 bumper clip. So the default order here is BIGGEST MONEY FIRST,
// and each row carries the three things needed to act on it — what the
// customer wants (every part, not a count), who took the call, and which
// supervisor put the price on it.
//
// Rows expand to the per-part breakdown, because "work out the parts needed"
// means seeing the actual part names, their source and whether anyone has
// priced them yet.
//
// Used by the Today screen's drill-down AND by one salesperson's report, so
// the two can never drift apart — same data, same reading.

import React from 'react';
import { inquiryApi } from '../../api/inquiryApi';
import {
  FiSearch,
  FiChevronDown,
  FiChevronRight,
  FiTrendingUp,
  FiClock,
  FiUser,
  FiTool,
  FiAlertCircle,
  FiInbox,
  FiRefreshCw,
  FiChevronLeft,
  FiPhone,
  FiMapPin,
  FiMessageSquare,
  FiXCircle,
  FiCheck,
} from 'react-icons/fi';

const fmtMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

// Anything at or above this is worth the admin's attention on sight.
const BIG_TICKET = 25000;

/** Parts still to be sourced — a cancelled or couldn't-source part is history,
 *  not outstanding work, and must not be read as something to go and buy. */
const livePartsOf = (row) =>
  (row.parts || []).filter((p) => !p.is_cancelled && !p.osm_unavailable);

const STAGE_TONE = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  live:    'bg-blue-50 text-blue-700 border-blue-200',
  waiting: 'bg-orange-50 text-orange-700 border-orange-200',
  hold:    'bg-violet-50 text-violet-700 border-violet-200',
  won:     'bg-emerald-50 text-emerald-700 border-emerald-200',
  loss:    'bg-slate-100 text-slate-500 border-slate-200',
};

const SOURCE_TONE = {
  cap:   'bg-emerald-50 text-emerald-700',
  osm:   'bg-blue-50 text-blue-700',
  stock: 'bg-purple-50 text-purple-700',
};

/** "Kunja → Sughail" — the full pricing chain, not just whoever touched it last. */
const PricedBy = ({ row, className = '' }) => {
  const chain = row.supervisors || [];
  if (chain.length === 0) {
    return (
      <span className={`inline-flex items-center gap-1 text-xs text-slate-400 ${className}`}>
        <FiAlertCircle className="w-3.5 h-3.5" /> not priced yet
      </span>
    );
  }
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 text-xs ${className}`}>
      <span className="text-slate-400">Priced by</span>
      {chain.map((name, i) => (
        <React.Fragment key={name}>
          {i > 0 && <span className="text-slate-300">→</span>}
          <span className={`font-semibold ${i === 0 ? 'text-slate-700' : 'text-violet-700'}`}>
            {name}
          </span>
        </React.Fragment>
      ))}
      {/* A second name means someone corrected the first price — say so, since
          that is exactly the case the admin needs to notice. */}
      {chain.length > 1 && (
        <span className="rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-bold text-violet-600">
          corrected
        </span>
      )}
    </span>
  );
};

const PartsTable = ({ parts }) => {
  if (!parts?.length) {
    return <p className="text-xs text-slate-400 py-2">No parts listed on this inquiry.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400">
            <th className="py-1.5 pr-3 font-bold">Part</th>
            <th className="py-1.5 pr-3 font-bold">Qty</th>
            <th className="py-1.5 pr-3 font-bold">Price</th>
            <th className="py-1.5 pr-3 font-bold">Source</th>
            <th className="py-1.5 pr-3 font-bold">Priced by</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {parts.map((p) => {
            const chain = [p.quoted_by, p.quote_edited_by].filter(Boolean);
            const uniq = [...new Set(chain)];
            // A part the customer dropped, or one the market could not supply,
            // is not something to go and source. It stays on the list as
            // history, but must never read like outstanding work.
            const dropped = p.is_cancelled || p.osm_unavailable;
            return (
              <tr key={p.id} className={`align-top ${dropped ? 'opacity-55' : ''}`}>
                <td className="py-2 pr-3">
                  <span className={`font-semibold ${dropped ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                    {p.name}
                  </span>
                  {p.is_cancelled && (
                    <span className="ml-2 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
                      cancelled
                    </span>
                  )}
                  {p.osm_unavailable && !p.is_cancelled && (
                    <span className="ml-2 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
                      couldn’t source
                    </span>
                  )}
                  {p.is_available === false && !dropped && (
                    <span className="ml-2 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold text-rose-600">
                      not available
                    </span>
                  )}
                  {p.notes && <p className="mt-0.5 text-[11px] text-slate-400">{p.notes}</p>}
                </td>
                <td className="py-2 pr-3 tabular-nums text-slate-500">{p.quantity}</td>
                <td className="py-2 pr-3">
                  {p.quoted_price ? (
                    <span className={`font-bold tabular-nums ${dropped ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                      {fmtMoney(p.quoted_price)}
                    </span>
                  ) : (
                    <span className={dropped ? 'text-slate-400' : 'text-amber-600 font-semibold'}>
                      {dropped ? '—' : 'awaiting price'}
                    </span>
                  )}
                  {/* Took the trade-in: the price charged is the exchange one,
                      with the full price struck through beside it. */}
                  {p.exchange_selected && p.list_price && p.list_price !== p.quoted_price && (
                    <>
                      <span className="ml-1.5 text-[10px] text-slate-400 line-through tabular-nums">
                        {fmtMoney(p.list_price)}
                      </span>
                      <span className="ml-1 rounded-full bg-teal-50 px-1.5 py-0.5 text-[10px] font-bold text-teal-700">
                        trade-in
                      </span>
                    </>
                  )}
                  {/* What it was first quoted at, when that differs — the
                      give-away on this line, visible without opening anything. */}
                  {!p.exchange_selected && p.first_quoted_price
                    && p.first_quoted_price !== p.quoted_price && (
                    <span className="ml-1.5 text-[10px] text-slate-400 line-through tabular-nums">
                      {fmtMoney(p.first_quoted_price)}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-3">
                  {p.source ? (
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${SOURCE_TONE[p.source] || 'bg-slate-100 text-slate-600'}`}>
                      {p.source_label}
                    </span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td className="py-2 pr-3">
                  {uniq.length ? (
                    <span className="text-slate-600">
                      {uniq.map((n, i) => (
                        <React.Fragment key={n}>
                          {i > 0 && <span className="text-slate-300"> → </span>}
                          <span className={i === 0 ? 'font-semibold' : 'font-semibold text-violet-700'}>{n}</span>
                        </React.Fragment>
                      ))}
                    </span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Acting from the review chair ─────────────────────────────────────────────
// This list surfaces stale inquiries precisely so someone DOES something
// about them — and that someone is the superadmin reviewing the day with the
// salesperson. Until now the review ended with "call your salesperson and ask
// them to close it"; these two actions finish it on the spot.

const FOLLOW_UP_OUTCOMES = [
  ['no_response', 'No response'],
  ['thinking', 'Customer is thinking'],
  ['price_high', 'Price is high'],
  ['callback_requested', 'Callback requested'],
  ['other', 'Other'],
];

const AdminActions = ({ row, onChanged }) => {
  const [mode, setMode] = React.useState(null); // null | 'follow' | 'close'
  const [outcome, setOutcome] = React.useState('no_response');
  const [note, setNote] = React.useState('');
  const [nextAt, setNextAt] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [saved, setSaved] = React.useState('');

  // Won and lost deals are done — nothing here applies. The backend enforces
  // the same line; hiding the buttons just stops a doomed round trip.
  if (!['pending', 'live', 'waiting'].includes(row.stage)) return null;

  const run = async (fn, savedMsg) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      setMode(null);
      setNote('');
      setNextAt('');
      setReason('');
      setSaved(savedMsg);
      setTimeout(() => setSaved(''), 2500);
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-slate-200 pt-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setMode(mode === 'follow' ? null : 'follow')}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold transition ${
            mode === 'follow'
              ? 'border-violet-500 bg-violet-50 text-violet-700'
              : 'border-slate-300 bg-white text-slate-600 hover:border-violet-300'
          }`}
        >
          <FiMessageSquare className="w-3.5 h-3.5" /> Add follow-up
        </button>
        <button
          type="button"
          onClick={() => setMode(mode === 'close' ? null : 'close')}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold transition ${
            mode === 'close'
              ? 'border-rose-500 bg-rose-50 text-rose-700'
              : 'border-slate-300 bg-white text-slate-600 hover:border-rose-300'
          }`}
        >
          <FiXCircle className="w-3.5 h-3.5" /> Close as lost
        </button>
        {saved && (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600">
            <FiCheck className="w-3.5 h-3.5" /> {saved}
          </span>
        )}
      </div>

      {mode === 'follow' && (
        <div className="mt-2.5 space-y-2 rounded-xl border border-violet-200 bg-white p-3">
          <div className="flex flex-wrap gap-1.5">
            {FOLLOW_UP_OUTCOMES.map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setOutcome(value)}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition ${
                  outcome === value
                    ? 'border-violet-500 bg-violet-500 text-white'
                    : 'border-slate-200 bg-white text-slate-500 hover:border-violet-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="What happened with this customer — the salesperson reads this too"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs focus:border-violet-400 focus:outline-none"
          />
          {/* The call-back date. This is what puts the customer on the
              salesperson's Follow-ups screen with a "Next" time — and counts
              them OVERDUE once it passes. A follow-up without a date is a
              note; with one, it is a reminder. */}
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[11px] font-semibold text-slate-500">
              Remind to call the customer on
            </label>
            <input
              type="datetime-local"
              value={nextAt}
              onChange={(e) => setNextAt(e.target.value)}
              className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 focus:border-violet-400 focus:outline-none"
            />
            {nextAt && (
              <button type="button" onClick={() => setNextAt('')}
                className="text-[11px] font-semibold text-slate-400 hover:text-rose-500 transition">
                Clear
              </button>
            )}
          </div>
          <button
            type="button"
            disabled={busy || !note.trim()}
            onClick={() =>
              run(() => inquiryApi.addFollowUp(row.inquiry_id, {
                outcome,
                note: note.trim(),
                ...(nextAt
                  ? { next_follow_up_at: new Date(nextAt).toISOString() }
                  : {}),
              }), 'Follow-up saved')}
            className="rounded-lg bg-violet-600 px-4 py-1.5 text-xs font-bold text-white transition hover:bg-violet-700 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save follow-up'}
          </button>
        </div>
      )}

      {mode === 'close' && (
        <div className="mt-2.5 space-y-2 rounded-xl border border-rose-200 bg-white p-3">
          <p className="text-xs font-semibold text-rose-700">
            This marks the deal LOST and tells {row.salesperson_name || 'the salesperson'} —
            say why, so nobody reopens it blind.
          </p>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Customer bought elsewhere / not reachable for a week"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs focus:border-rose-400 focus:outline-none"
          />
          <button
            type="button"
            disabled={busy || !reason.trim()}
            onClick={() =>
              run(() => inquiryApi.closeInquiry(row.inquiry_id, reason.trim()),
                'Closed as lost')}
            className="rounded-lg bg-rose-600 px-4 py-1.5 text-xs font-bold text-white transition hover:bg-rose-700 disabled:opacity-50"
          >
            {busy ? 'Closing…' : 'Close as lost'}
          </button>
        </div>
      )}

      {error && <p className="mt-1.5 text-xs font-semibold text-rose-600">{error}</p>}
    </div>
  );
};

const Row = ({ row, open, onToggle, onChanged }) => {
  const value = Number(row.value);
  const big = Number.isFinite(value) && value >= BIG_TICKET;

  return (
    <div className={`rounded-2xl border bg-white shadow-sm transition-all ${
      big ? 'border-violet-200 ring-1 ring-violet-100' : 'border-slate-200'
    }`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-4 flex items-start gap-3"
        aria-expanded={open}
      >
        {/* Money first — it is the reason this list is sorted the way it is */}
        <div className="shrink-0 text-right min-w-[92px]">
          <p className={`text-lg font-black leading-none tabular-nums ${
            row.value ? 'text-slate-900' : 'text-slate-300'
          }`}>
            {row.value ? fmtMoney(row.value) : '—'}
          </p>
          {row.discount_given && (
            <p className="mt-1 text-[10px] font-semibold text-rose-500">
              −{fmtMoney(row.discount_given)} given
            </p>
          )}
          {!row.value && (
            <p className="mt-1 text-[10px] font-semibold text-amber-600">no price yet</p>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-slate-800 truncate">{row.customer_name || 'Unknown'}</span>
            {row.is_urgent && (
              <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">
                URGENT
              </span>
            )}
            {big && (
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">
                <FiTrendingUp className="w-3 h-3" /> BIG TICKET
              </span>
            )}
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${
              STAGE_TONE[row.stage] || STAGE_TONE.pending
            }`}>
              {row.status_label}
            </span>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            {row.customer_phone && (
              <span className="inline-flex items-center gap-1">
                <FiPhone className="w-3 h-3" /> {row.customer_phone}
              </span>
            )}
            {row.customer_location && (
              <span className="inline-flex items-center gap-1">
                <FiMapPin className="w-3 h-3" /> {row.customer_location}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <FiClock className="w-3 h-3" /> {row.created_time}
            </span>
          </div>

          <p className="mt-1.5 text-sm font-semibold text-slate-600">{row.car || '—'}</p>

          {/* The parts still to be arranged. Cancelled and couldn't-source
              parts are deliberately left out of this line — it is read as a
              shopping list, and a dropped part on it sends someone out to buy
              something nobody wants. They are still visible on expand. */}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <FiTool className="w-3.5 h-3.5 text-slate-300" />
            {livePartsOf(row).slice(0, 4).map((p) => (
              <span key={p.id} className="rounded-lg bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                {p.name}{p.quantity > 1 ? ` ×${p.quantity}` : ''}
              </span>
            ))}
            {livePartsOf(row).length > 4 && (
              <span className="text-[11px] font-semibold text-slate-400">
                +{livePartsOf(row).length - 4} more
              </span>
            )}
            {livePartsOf(row).length === 0 && (
              <span className="text-[11px] font-semibold text-slate-400">
                no parts outstanding
              </span>
            )}
            {row.parts_awaiting_quote > 0 && (
              <span className="rounded-lg bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">
                {row.parts_awaiting_quote} awaiting price
              </span>
            )}
            {row.parts_cancelled_count > 0 && (
              <span className="rounded-lg bg-slate-200 px-2 py-0.5 text-[11px] font-bold text-slate-600">
                {row.parts_cancelled_count} dropped
                {row.cancelled_value ? ` · ${fmtMoney(row.cancelled_value)}` : ''}
              </span>
            )}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 pt-2">
            <span className="inline-flex items-center gap-1 text-xs">
              <FiUser className="w-3.5 h-3.5 text-slate-300" />
              <span className="text-slate-400">Sales</span>
              <span className="font-semibold text-slate-700">{row.salesperson_name || '—'}</span>
            </span>
            <PricedBy row={row} />
          </div>
        </div>

        <span className="shrink-0 text-slate-300 mt-1">
          {open ? <FiChevronDown className="w-5 h-5" /> : <FiChevronRight className="w-5 h-5" />}
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/60 rounded-b-2xl space-y-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">
              Parts asked for
            </p>
            <PartsTable parts={row.parts} />
          </div>

          {row.quote_history?.length > 1 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">
                Price trail
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                {row.quote_history.map((h, i) => (
                  <React.Fragment key={`${h.at}-${i}`}>
                    {i > 0 && <span className="text-slate-300">→</span>}
                    <span className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px]">
                      <span className="font-bold tabular-nums text-slate-700">
                        {h.amount ? fmtMoney(h.amount) : '—'}
                      </span>
                      {h.by && <span className="ml-1 text-slate-400">by {h.by}</span>}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
            <span>Inquiry <span className="font-semibold text-slate-700">#{row.inquiry_id}</span></span>
            <span>Came in <span className="font-semibold text-slate-700">{row.created_at}</span></span>
            {row.quoted_at && (
              <span>Priced <span className="font-semibold text-slate-700">{row.quoted_at}</span></span>
            )}
            {row.balance_due && Number(row.balance_due) > 0 && (
              <span className="font-semibold text-amber-600">
                Balance due {fmtMoney(row.balance_due)}
              </span>
            )}
          </div>

          <AdminActions row={row} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, tone = 'text-slate-900' }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
    <p className={`text-lg font-black leading-tight tabular-nums ${tone}`}>{value}</p>
  </div>
);

/**
 * props:
 *   period        'today' | 'yesterday' | 'week' | 'month' | 'year' | 'all'
 *   salespersonId scope to one salesperson (optional)
 *   pageSize      rows per page (default 25)
 *   onLoaded      (data) => void — lets a parent reuse the rows, e.g. for Excel
 */
const InquiryDayList = ({ period = 'today', salespersonId, pageSize = 25, onLoaded }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [sort, setSort] = React.useState('value');
  const [q, setQ] = React.useState('');
  const [debouncedQ, setDebouncedQ] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [openId, setOpenId] = React.useState(null);
  // Bumped after an admin action (follow-up, close) so the row re-reads its
  // new status instead of showing a stale stage next to a "Saved" tick.
  const [reloadKey, setReloadKey] = React.useState(0);

  // Typing shouldn't fire a request per keystroke.
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Any change of what we're looking AT resets to page 1 — otherwise a
  // narrower search lands on a page that no longer exists.
  //
  // This is done DURING RENDER, not in an effect, and that matters. As an
  // effect it would reset the page one commit too late: the fetch effect in
  // the same commit still sees the old page, fires a request for (new filter,
  // page 3), and only then does the reset trigger the real (new filter,
  // page 1) request. Two requests, and the stale one can land second and
  // paint an empty list — which looks exactly like "nothing is showing".
  // Adjusting state in render makes React re-run before committing, so only
  // the correct request is ever made.
  const filterKey = `${period}|${salespersonId ?? ''}|${debouncedQ}|${sort}`;
  const [seenFilter, setSeenFilter] = React.useState(filterKey);
  if (seenFilter !== filterKey) {
    setSeenFilter(filterKey);
    setPage(1);
  }

  const onLoadedRef = React.useRef(onLoaded);
  React.useEffect(() => { onLoadedRef.current = onLoaded; }, [onLoaded]);

  // Belt and braces: even with the reset above, a slow response from an
  // earlier keystroke must never overwrite a newer one. Only the most
  // recently fired request is allowed to write state.
  const reqIdRef = React.useRef(0);
  // The page whose rows are actually on screen, so a failed fetch can put the
  // pager back where the reader is rather than where they tried to go.
  const lastGoodPageRef = React.useRef(1);

  React.useEffect(() => {
    const reqId = ++reqIdRef.current;
    const isCurrent = () => reqId === reqIdRef.current;
    setLoading(true);
    inquiryApi
      .getDay({ period, salespersonId, q: debouncedQ, sort, page, pageSize })
      .then((res) => {
        if (!isCurrent()) return;
        setData(res.data);
        setError(null);
        lastGoodPageRef.current = res.data?.page ?? page;
        onLoadedRef.current?.(res.data);
      })
      .catch((e) => {
        if (!isCurrent()) return;
        setError(e?.response?.data?.error || e?.message || 'Could not load the inquiries');
        // Snap back to the page actually on screen. Without this, `page` has
        // already advanced while the rows have not, so the next Next click
        // jumps straight over the page that failed and those inquiries are
        // never seen by anyone.
        setPage(lastGoodPageRef.current);
      })
      .finally(() => { if (isCurrent()) setLoading(false); });
    // Invalidates the in-flight request when the inputs change or the
    // component goes away, so a late response can't write to dead state.
    return () => { reqIdRef.current += 1; };
  }, [period, salespersonId, debouncedQ, sort, page, pageSize, reloadKey]);

  const items = data?.items || [];
  const totals = data?.totals || {};
  const count = data?.count ?? 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Inquiries" value={count} />
        <Stat label="Value on the table" value={fmtMoney(totals.value)} tone="text-violet-700" />
        <Stat label="Awaiting a price" value={totals.awaiting_quote_count ?? 0}
              tone={(totals.awaiting_quote_count ?? 0) > 0 ? 'text-amber-600' : 'text-slate-900'} />
        <Stat label="Already sold" value={`${totals.sold_count ?? 0} · ${fmtMoney(totals.sold_value)}`}
              tone="text-emerald-700" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customer, phone, car, part or staff…"
            className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-violet-400 focus:outline-none"
          />
        </div>
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
          {[
            { id: 'value', label: 'Biggest first' },
            { id: 'recent', label: 'Newest first' },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSort(s.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                sort === s.id ? 'bg-violet-600 text-white' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        {loading && <FiRefreshCw className="w-4 h-4 animate-spin text-slate-400" />}
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50">
            <FiInbox className="h-6 w-6 text-slate-300" />
          </div>
          <p className="font-bold text-slate-700">
            {debouncedQ ? 'Nothing matches that search' : 'No inquiries in this period'}
          </p>
          <p className="mt-1 text-sm text-slate-400">
            {debouncedQ
              ? 'Try a customer name, phone number, car or part.'
              : 'Anything a salesperson creates will appear here straight away.'}
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-2.5">
          {items.map((row) => (
            <Row
              key={row.inquiry_id}
              row={row}
              open={openId === row.inquiry_id}
              onToggle={() => setOpenId((prev) => (prev === row.inquiry_id ? null : row.inquiry_id))}
              onChanged={() => setReloadKey((k) => k + 1)}
            />
          ))}
        </div>
      )}

      {(data?.num_pages ?? 1) > 1 && (
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold text-slate-600 disabled:opacity-40 hover:bg-slate-50"
          >
            <FiChevronLeft className="w-3.5 h-3.5" /> Previous
          </button>
          {/* Reads the page actually rendered, not the one last requested. */}
          <span className="text-xs font-semibold text-slate-500">
            Page {data.page ?? page} of {data.num_pages} · {count} inquiries
          </span>
          <button
            type="button"
            disabled={page >= (data?.num_pages ?? 1) || loading}
            onClick={() => setPage((p) => p + 1)}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold text-slate-600 disabled:opacity-40 hover:bg-slate-50"
          >
            Next <FiChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default InquiryDayList;
